import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {HasSubscriptions} from '../../has-subscriptions';
import {CommunicationService} from '../../../services/communication.service';
import {Listing} from '../../../classes/listing';
import {PreferencesService} from '../../../services/preferences.service';
import {ListingsService} from '../../../services/listings.service';
import {SortBy, ViewModes} from '../../../classes/enums';
import {SelectOption} from '../../forms/select/select.component';
import {PinInfo} from '../../../classes/pin-info';
import {ListingCardComponent} from '../../../shared/components/listing-card/listing-card.component';
import {CacheService} from '../../../services/cache.service';
import {Pagination} from '../../../classes/pagination';
import {Office} from '../../../classes/office';
import {Helper} from '../../../classes/helper';
import {DomManipulationService} from '../../../services/dom-manipulation/dom-manipulation.service';
import {OfficeService} from '../../../services/office.service';
import {SearchResult} from '../../../classes/object-response';
import { debounceTime } from 'rxjs/operators';
import {Filters} from '../../../classes/user-preferences';

@Component({
  selector: 'app-gallery-view-desktop',
  templateUrl: './gallery-view-desktop.component.html',
  styleUrls: ['./gallery-view-desktop.component.scss']
})
export class GalleryViewDesktopComponent extends HasSubscriptions implements AfterViewInit, OnInit, OnChanges {
  @Input() showSpecificListings: Listing[] = [];
  @Input() showSearchBar: boolean = true;
  @Input() showRelatedOffice: boolean = false;
  @Input() startingPageNumber: number = 0;
  @Input() isDoneFetchingListings: boolean = false;

  @ViewChild('container') container: ElementRef;
  @ViewChildren(ListingCardComponent, {'read' : ListingCardComponent}) listingCards: QueryList<ListingCardComponent>;

  pagination: Pagination = new Pagination(0, 20);
  filters: Filters = new Filters();
  activeViewMode: ViewModes;
  ViewModes = ViewModes;
  sortForm: FormGroup;
  showOnlySelectedCluster: boolean = false;
  pins: Array<PinInfo> = [];

  // Sample set of listings generated from pin information before we've retrieved the full listing information from the server
  listOfSortedFakeListings: Listing[] = [];
  showingListings: Array<Listing> = [];
  relatedOffice: Office;
  isPaginated: boolean = false;

  pages: Array<{pageNumber: number, listingIds: Array<string>}> = [];

  sortOptions: SelectOption[] = [
    {
      key: 'gallery.sortby.newest',
      value: SortBy.NEWEST
    } as SelectOption,
    {
      key: 'gallery.sortby.oldest',
      value: SortBy.OLDEST
    } as SelectOption,
    {
      key: 'gallery.sortby.priceLowToHigh',
      value: SortBy.PRICE_LOW_TO_HIGH
    } as SelectOption,
    {
      key: 'gallery.sortby.priceHighToLow',
      value: SortBy.PRICE_HIGH_TO_LOW
    } as SelectOption
  ];

  Listing = Listing;

  @ViewChild('galleryContainer') galleryContainerRef: ElementRef;

  constructor(private fb: FormBuilder,
              private communicationService: CommunicationService,
              private preferencesService: PreferencesService,
              private listingsService: ListingsService,
              private cache: CacheService,
              private domManipulationService: DomManipulationService,
              private officeService: OfficeService
  ) {
    super();
    this.sortForm = this.fb.group({
      sort: SortBy.NEWEST
    });
  }

  ngOnInit() {
    this.addSub(this.communicationService.listings.listingsClicked.subscribe((listings) => {
      this.showSpecificListings = listings;
      this.sortAndPaginate();
    }));

    this.addSub(this.preferencesService.filtersChanged.subscribe((filters) => {
      this.filters = filters;
    }));

    this.sortForm.controls['sort'].setValue(this.preferencesService.getGallerySorting());

    this.addSub(this.preferencesService.viewModesChanged.subscribe(mode => {
      this.activeViewMode = mode;
      if (this.activeViewMode === ViewModes.GALLERY) {
        // fetch the related office
        const pagination: Pagination = new Pagination(0, 1);
        if (this.showingListings.length > 0) {
          this.officeService.getRelatedOffice(this.showingListings[0].lat, this.showingListings[0].lng, pagination).then((offices: SearchResult<Office[]>) => {
            if (offices.results.length > 0) {
              this.relatedOffice = offices.results[0];
            }
          });
        }
      }
    }));
  }

  ngAfterViewInit() {
    // Any time the sort changes, sort the listings. Skip the first one when the component is initialized.
    this.addSub(this.sortForm.valueChanges.pipe(debounceTime(200)).subscribe(value => {
      this.preferencesService.setGallerySorting(value.sort as SortBy);
      this.sortAndPaginate(true)
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showSpecificListings) this.sortAndPaginate();
    if (changes.startingPageNumber) this.pagination.pageNumber = changes.startingPageNumber.currentValue;
  }

  onHoverListingCard(event) {
    this.communicationService.map.highlightMarker.next(event);
  }

  onGalleryCardClicked(listing) {
    this.communicationService.map.highlightMarker.next({action: 'leave', listingId: listing.listingId});
    this.preferencesService.addNextListings(this.showingListings.map((l) => ({listingId: l.listingId, detailUrl: l.detailUrl})));
  }

  async sortAndPaginate(isSort: boolean = false) {
    this.showOnlySelectedCluster = this.showSpecificListings.length > 1;
    this.showSpecificListings = this.sortListings(this.showSpecificListings);
    this.listOfSortedFakeListings = this.sortListings(this.listOfSortedFakeListings);
    this.buildPagination();
    if (isSort || this.showOnlySelectedCluster) {
      // if a cluster is clicked, we always show the first page
      await this.changePage(0);
    }
  }

  highlightCard(listingId: string) {
    // give angular some time to render the new cards
    setTimeout(() => {
      this.listingCards.forEach((card) => {
        card.setHighlight(listingId);
      })
    }, 100);
  }

  /**
   * Resets the pagination and moves the page into view of the container for the pagination
   */
  reset() {
    this.pages = [];
    this.pagination.pageNumber = this.startingPageNumber;
  }

  /**
   *
   * @param pageNumber
   */
  async changePage(pageNumber: number) {
    this.pagination.pageNumber = pageNumber;
    // this.pagination.pageNumber = 0;
    if (this.activeViewMode !== ViewModes.GALLERY) {
      this.domManipulationService.scrollIntoView(this.container, true);
    }
    if (this.showOnlySelectedCluster) {
      // TODO: why cant we use pagination.offset here
      this.showingListings = this.showSpecificListings.slice(this.pagination.offset, Math.min(this.showSpecificListings.length, this.pagination.offset + this.pagination.limit));
    } else {
      await this.fetchAndDisplayPage();
    }
  }

  private sortListings(listings): Array<Listing> {
    const sortBy = this.sortForm.get('sort').value as SortBy;
    return listings.sort((o1: Listing, o2: Listing) => Listing.sortBy(o1, o2, sortBy, this.filters.priceType === 1));
  }

  // TODO: maybe cache what was fetched
  private async fetchAndDisplayPage() {
    const listingIds = this.listOfSortedFakeListings.slice(this.pagination.offset, Math.min(this.listOfSortedFakeListings.length, this.pagination.offset + this.pagination.limit)).map(it => it.listingId).join(',');
    if (listingIds && listingIds.length) {
      const response =  await this.listingsService.getListingsPreview(listingIds, this.activeViewMode === ViewModes.GALLERY);
      this.showingListings = this.sortListings(response.results);
      if (response.office) {
        this.relatedOffice = response.office;
        this.relatedOffice.telephone = Helper.formatPhoneNumber(this.relatedOffice.telephone);
      }
    }
    else this.showingListings = [];
  }

  private buildPagination() {
    this.reset();
    this.isPaginated = this.showOnlySelectedCluster ? this.showSpecificListings.length > this.pagination.limit : this.listOfSortedFakeListings.length > this.pagination.limit;
    // this.pagination.totalPages = Math.ceil((this.showOnlySelectedCluster ? this.showSpecificListings.length : this.listOfSortedFakeListings.length) / this.pagination.limit);
    this.pagination.setTotalResults(this.showOnlySelectedCluster ? this.showSpecificListings.length : this.listOfSortedFakeListings.length);
    const itemsToShow = this.showOnlySelectedCluster ? this.showSpecificListings.slice(0, this.pagination.limit) : this.listOfSortedFakeListings.slice(0, this.pagination.limit);
    this.pages.push({pageNumber: 0, listingIds: itemsToShow.map(it => it.listingId)});
    for (let i = 1; i < this.pagination.totalPages; i++) {
      if (this.showOnlySelectedCluster) {
        this.pages.push({pageNumber: i, listingIds: this.showSpecificListings.slice(this.pagination.limit * i, this.pagination.limit * (i + 1)).map(it => it.listingId)});
      } else {
        this.pages.push({pageNumber: i, listingIds: this.listOfSortedFakeListings.slice(this.pagination.limit * i, this.pagination.limit * (i + 1)).map(it => it.listingId)});
      }
    }
  }

  @Input()
  set listingData(pins: Array<PinInfo>) {
    if (!pins) return;
    this.pins = pins;
    this.showOnlySelectedCluster = false;
    this.showSpecificListings = [];
    this.listOfSortedFakeListings = this.sortListings(this.getListingsFromPins());
    this.buildPagination();

    // Make sure we scroll to the top anytime we get new listings
    this.domManipulationService.scrollToTop(this.galleryContainerRef);
    // Check if we're coming back to the map from a listing detail and need to highlight that listing.
    const selectedListingId = this.cache.get<string>(this.cache.KeySelectedListingId, true);
    if (selectedListingId) {
      const showListing = new Listing();
      showListing.listingId = selectedListingId;
      this.showSpecificListings = [showListing];
      this.highlightListing = selectedListingId;
    }
    else {
      this.pagination.pageNumber = this.startingPageNumber;
      this.fetchAndDisplayPage();
    }
  }

  @Input()
  set highlightListing(listingId: string) {
    // if a single item is clicked, we need to navigate to the page where that item is
    // and highlight it
    if (!listingId) return;

    let isItemInGallery = false;

    for (const item of this.showingListings) {
      if (listingId === item.listingId) {
        isItemInGallery = true;
        break;
      }
    }

    if (isItemInGallery) {
      this.highlightCard(listingId);
    } else {
      let pageNumber = 1;
      for (const page of this.pages) {
        if (page.listingIds.indexOf(listingId) >= 0) {
          pageNumber = page.pageNumber;
          break;
        }
      }
      this.changePage(pageNumber).then(() => {
        this.highlightCard(listingId);
      });
    }
    this.communicationService.map.highlightMarker.next({action: 'enter', listingId: listingId});
  }

  private getListingsFromPins() {
    if (!this.pins) return;
    return this.pins.map(p => new Listing({ listingId: p.listingId, listPrice: p.listPrice, listingDate: p.listingDate, pricePerSqFt: p.pricePerSqft }));
  }
}
